import React, { useState } from 'react';
import type { Inspection } from '../../types';
import { formatDate } from '../../utils/scheduling';

interface Props {
  inspections: Inspection[];
  onClose: () => void;
}

export default function ConcernOverlay({ inspections, onClose }: Props) {
  const [expandedId, setExpandedId] = useState<number | null>(null);

  const withConcerns = inspections
    .filter((i) => i.NumberOfConcerns >= 1)
    .sort((a, b) => b.DateInspected.localeCompare(a.DateInspected));

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 pt-10 pb-6 px-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] flex flex-col slide-in-up">
        {/* Header */}
        <div className="flex items-center justify-between px-6 pt-5 pb-4 border-b border-gray-100">
          <div>
            <h2 className="text-lg font-bold text-red-600 flex items-center gap-2">
              ❗ Concerns Summary
            </h2>
            <p className="text-sm text-gray-500 mt-0.5">
              Inspections with ≥1 concern · {withConcerns.length} record{withConcerns.length !== 1 ? 's' : ''}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1.5 rounded-lg hover:bg-gray-100 transition"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {withConcerns.length === 0 ? (
            <div className="text-center py-12 text-gray-400">
              No inspections with concerns found.
            </div>
          ) : (
            withConcerns.map((insp, i) => (
              <div
                key={i}
                className="border border-red-100 rounded-xl overflow-hidden"
              >
                {/* Summary row */}
                <div className="bg-red-50 px-4 py-3">
                  <div className="flex items-start justify-between gap-2 flex-wrap">
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-semibold text-gray-900">{insp.Area}</span>
                        <span className="bg-[#EBF7E0] text-[#4C9E00] text-xs px-2 py-0.5 rounded-full font-medium">
                          {insp.AreaType}
                        </span>
                      </div>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {formatDate(insp.DateInspected)} · {insp.InspectedBy}
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="bg-red-100 text-red-700 text-xs font-bold px-2 py-0.5 rounded-full">
                        {insp.NumberOfConcerns} concern{insp.NumberOfConcerns !== 1 ? 's' : ''}
                      </span>
                      <button
                        onClick={() =>
                          setExpandedId((prev) => (prev === i ? null : i))
                        }
                        className="text-xs text-[#4C9E00] font-medium px-2 py-1 rounded-md hover:bg-[#EBF7E0] transition border border-[#4C9E00]/30"
                      >
                        {expandedId === i ? '▲ Hide' : 'Full Report'}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Expanded full report */}
                {expandedId === i && (
                  <div className="bg-white px-4 py-4 border-t border-red-100 space-y-3">
                    <h4 className="text-xs font-bold text-gray-500 uppercase tracking-wide">
                      Full Inspection Detail
                    </h4>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
                      <div>
                        <span className="text-xs text-gray-400">Area</span>
                        <p className="font-medium text-gray-800">{insp.Area}</p>
                      </div>
                      <div>
                        <span className="text-xs text-gray-400">Area Type</span>
                        <p className="font-medium text-gray-800">{insp.AreaType}</p>
                      </div>
                      <div>
                        <span className="text-xs text-gray-400">Inspector</span>
                        <p className="font-medium text-gray-800">{insp.InspectedBy}</p>
                      </div>
                      <div>
                        <span className="text-xs text-gray-400">Supervisor</span>
                        <p className="font-medium text-gray-800">{insp.Supervisor}</p>
                      </div>
                      <div>
                        <span className="text-xs text-gray-400">Date Inspected</span>
                        <p className="font-medium text-gray-800">
                          {formatDate(insp.DateInspected)}
                        </p>
                      </div>
                      <div>
                        <span className="text-xs text-gray-400">Period</span>
                        <p className="font-medium text-gray-800">
                          {insp.Period} {insp.Year}
                        </p>
                      </div>
                      <div>
                        <span className="text-xs text-gray-400">Ground Concerns</span>
                        <p className="font-bold text-red-600">{insp.NumberOfConcerns}</p>
                      </div>
                      <div>
                        <span className="text-xs text-gray-400">Red Taped</span>
                        <p
                          className={`font-medium ${
                            insp.RedTaped === 'Yes'
                              ? 'text-red-600'
                              : 'text-gray-800'
                          }`}
                        >
                          {insp.RedTaped}
                        </p>
                      </div>
                      <div>
                        <span className="text-xs text-gray-400">Highback Inspected</span>
                        <p className="font-medium text-gray-800">
                          {insp.HighbackInspected}
                        </p>
                      </div>
                    </div>
                    {insp.Comment && (
                      <div className="border-t border-gray-100 pt-3">
                        <span className="text-xs text-gray-400">Comments</span>
                        <p className="text-sm text-gray-700 mt-0.5 italic">
                          "{insp.Comment}"
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
